import { Box, IconButton, Typography } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useEffect, useRef, useState } from "react";
import { chatbotGradients, chatbotPalette } from "./chatbotTheme";

export default function ChatComposer({ disabled, onSubmit }) {
  const [draft, setDraft] = useState("");
  const textAreaRef = useRef(null);

  const resizeTextArea = () => {
    const el = textAreaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "0px";
    const nextHeight = Math.min(Math.max(el.scrollHeight, 24), 84);
    el.style.height = `${nextHeight}px`;
  };

  useEffect(() => {
    resizeTextArea();
  }, [draft]);

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || disabled) {
      return;
    }

    setDraft("");
    await onSubmit(trimmed);
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      await handleSend();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-end" }}>
        <Box
          sx={{
            flex: 1,
            borderRadius: 3.5,
            backgroundColor: "rgba(255,255,255,0.06)",
            border: `1px solid ${chatbotPalette.border}`,
            px: 1.25,
            py: 1,
            transition: "background-color 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
            "&:focus-within": {
              backgroundColor: "rgba(255,255,255,0.08)",
              boxShadow: "0 0 0 1px rgba(44, 204, 211, 0.26)",
              borderColor: "rgba(189, 244, 241, 0.45)",
            },
          }}
        >
          <Box
            component="textarea"
            ref={textAreaRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="ถามเรื่อง OEE, downtime, availability..."
            disabled={disabled}
            aria-label="ถามNeo"
            rows={1}
            sx={{
              width: "100%",
              resize: "none",
              border: "none",
              outline: "none",
              p: 0,
              m: 0,
              backgroundColor: "transparent",
              color: chatbotPalette.textMain,
              fontFamily: "inherit",
              fontSize: 16,
              lineHeight: 1.4,
              minHeight: 24,
              maxHeight: 84,
              overflowY: "auto",
              "&::placeholder": {
                color: chatbotPalette.textMuted,
                opacity: 1,
              },
            }}
          />
        </Box>
        <IconButton
          aria-label="ส่งข้อความ"
          onClick={handleSend}
          disabled={disabled || !draft.trim()}
          sx={{
            width: 50,
            height: 50,
            borderRadius: 3,
            background: chatbotGradients.sendButton,
            color: "#10261f",
            boxShadow: "0 14px 24px rgba(44, 204, 211, 0.24)",
            transition: "transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
            "&:hover": {
              background: chatbotGradients.launcherHover,
              transform: "translateY(-1px)",
              boxShadow: "0 18px 30px rgba(44, 204, 211, 0.34)",
            },
            "&.Mui-disabled": {
              background: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.35)",
            },
          }}
        >
          <SendRoundedIcon />
        </IconButton>
      </Box>
      <Typography sx={{ color: chatbotPalette.textMuted, fontSize: 11, px: 0.25 }}>
        Enter เพื่อส่ง, Shift+Enter เพื่อขึ้นบรรทัดใหม่
      </Typography>
    </Box>
  );
}